import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface Project {
  id: string;
  title: string;
  description?: string;
  genre?: string;
  tempo?: number;
  status: 'DRAFT' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  owner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  collaborators: Array<{
    id: string;
    role: 'OWNER' | 'EDITOR' | 'REVIEWER' | 'VIEWER';
    status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'REMOVED';
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  }>;
  sessions: Array<{
    id: string;
    filename: string;
    version: number;
    isCurrent: boolean;
    createdAt: string;
  }>;
  _count: {
    sessions: number;
    collaborators: number;
  };
}

interface CreateProjectData {
  title: string;
  description?: string;
  genre?: string;
  tempo?: number;
}

interface UpdateProjectData extends Partial<CreateProjectData> {
  status?: Project['status'];
}

interface Invitation {
  id: string;
  role: 'OWNER' | 'EDITOR' | 'REVIEWER' | 'VIEWER';
  invitedAt: string;
  project: {
    id: string;
    title: string;
    description?: string;
    genre?: string;
    status: Project['status'];
    createdAt: string;
    owner: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  invitations: Invitation[];
  loading: boolean;
  getProjects: () => Promise<void>;
  getProject: (id: string) => Promise<Project | null>;
  createProject: (data: CreateProjectData) => Promise<Project | null>;
  updateProject: (id: string, data: UpdateProjectData) => Promise<Project | null>;
  deleteProject: (id: string) => Promise<void>;
  inviteCollaborator: (projectId: string, email: string, role: string) => Promise<void>;
  respondToInvitation: (invitationId: string, response: 'ACCEPTED' | 'DECLINED') => Promise<void>;
  getInvitations: () => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);

  const getProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects');
      console.log('Projects response:', response.data);
      setProjects(response.data.data.projects);
    } catch (error: any) {
      // Only show error if user is authenticated (not a 401)
      if (error.response?.status !== 401) {
        const message = error.response?.data?.message || 'Failed to load projects';
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getProject = async (id: string): Promise<Project | null> => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${id}`);
      const project = response.data.data.project;
      setCurrentProject(project);
      return project;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to load project';
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (data: CreateProjectData): Promise<Project | null> => {
    try {
      setLoading(true);
      const response = await api.post('/projects', data);
      const project = response.data.data.project;
      
      setProjects(prev => [project, ...prev]);
      toast.success('Project created successfully!');
      return project;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create project';
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (id: string, data: UpdateProjectData): Promise<Project | null> => {
    try {
      setLoading(true);
      const response = await api.put(`/projects/${id}`, data);
      const project = response.data.data.project;
      
      setProjects(prev => prev.map(p => p.id === id ? project : p));
      setCurrentProject(project);
      toast.success('Project updated successfully!');
      return project;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update project';
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id: string) => {
    try {
      setLoading(true);
      await api.delete(`/projects/${id}`);
      
      setProjects(prev => prev.filter(p => p.id !== id));
      if (currentProject?.id === id) {
        setCurrentProject(null);
      }
      toast.success('Project deleted successfully!');
    } catch (error: any) {
      console.error('Delete project error:', error);
      console.error('Error response:', error.response);
      const message = error.response?.data?.message || 'Failed to delete project';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const inviteCollaborator = async (projectId: string, email: string, role: string) => {
    try {
      await api.post(`/projects/${projectId}/collaborators`, { email, role });
      toast.success('Invitation sent successfully!');
      
      // Refresh the current project to show the updated collaborators
      if (currentProject?.id === projectId) {
        await getProject(projectId);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send invitation';
      toast.error(message);
    }
  };

  const respondToInvitation = async (invitationId: string, response: 'ACCEPTED' | 'DECLINED') => {
    try {
      await api.post(`/projects/invitations/${invitationId}/respond`, { response });
      
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      toast.success(`Invitation ${response.toLowerCase()} successfully!`);
      
      // Refresh projects if accepted
      if (response === 'ACCEPTED') {
        await getProjects();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to respond to invitation';
      toast.error(message);
    }
  };

  const getInvitations = async () => {
    try {
      const response = await api.get('/projects/invitations/pending');
      setInvitations(response.data.data.invitations);
    } catch (error: any) {
      // Only show error if user is authenticated (not a 401)
      if (error.response?.status !== 401) {
        const message = error.response?.data?.message || 'Failed to load invitations';
        toast.error(message);
      }
    }
  };

  // Load initial data only when user is authenticated
  useEffect(() => {
    if (user && !authLoading) {
      getProjects();
      getInvitations();
    }
  }, [user, authLoading]);

  const value = {
    projects,
    currentProject,
    invitations,
    loading,
    getProjects,
    getProject,
    createProject,
    updateProject,
    deleteProject,
    inviteCollaborator,
    respondToInvitation,
    getInvitations,
    setCurrentProject,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};